import React, { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate, useParams } from "react-router-dom";
import { getTemplate, createTemplate, updateTemplate, uploadTemplateImage } from "../../../data/requests/template-requests";
import { AdminLayout } from "../shared/layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { toast } from "@/components/ui/use-toast";
import { ArrowLeft, Upload, Save } from "lucide-react";

export function TemplateManageScreen() {
  const { id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const isEdit = !!id && id !== "new";

  const [formData, setFormData] = useState({
    name: "",
    description: "",
    imageUrl: "",
    thumbnailUrl: "",
    webhookUrl: "",
    webhookMethod: "POST",
    webhookHeaders: "{}",
    webhookPayload: "{}",
    responseMapping: '{"outputImage":"data.result.url"}',
    prompt: "",
    tags: "[]",
    category: "",
    isPublished: true,
  });

  const [uploading, setUploading] = useState(false);
  const [uploadingThumbnail, setUploadingThumbnail] = useState(false);

  const { data: template } = useQuery({
    queryKey: ["template", id],
    queryFn: () => getTemplate(id).then(res => res.data),
    enabled: isEdit,
  });

  useEffect(() => {
    if (template) {
      setFormData({
        name: template.name || "",
        description: template.description || "",
        imageUrl: template.imageUrl || "",
        thumbnailUrl: template.thumbnailUrl || "",
        webhookUrl: template.webhookUrl || "",
        webhookMethod: template.webhookMethod || "POST",
        webhookHeaders: template.webhookHeaders || "{}",
        webhookPayload: template.webhookPayload || "{}",
        responseMapping: template.responseMapping || '{"outputImage":"data.result.url"}',
        prompt: template.prompt || "",
        tags: template.tags || "[]",
        category: template.category || "",
        isPublished: template.isPublished !== false,
      });
    }
  }, [template]);

  const saveMutation = useMutation({
    mutationFn: (data) => {
      if (isEdit) {
        return updateTemplate(id, data);
      }
      return createTemplate(data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries(["admin-templates"]);
      toast({
        title: "Success",
        description: `Template ${isEdit ? "updated" : "created"} successfully`,
      });
      navigate("/admin/templates");
    },
    onError: (error) => {
      toast({
        title: "Error",
        description: error.response?.data?.message || "Failed to save template",
        variant: "destructive",
      });
    },
  });

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploading(true);
    try {
      const reader = new FileReader();
      reader.onloadend = async () => {
        const base64 = reader.result;
        const response = await uploadTemplateImage(base64);
        setFormData({
          ...formData,
          imageUrl: response.data.url,
          thumbnailUrl: response.data.thumbnailUrl,
        });
        toast({
          title: "Success",
          description: "Template image uploaded successfully",
        });
      };
      reader.readAsDataURL(file);
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to upload template image",
        variant: "destructive",
      });
    } finally {
      setUploading(false);
    }
  };

  const handleThumbnailUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploadingThumbnail(true);
    try {
      const reader = new FileReader();
      reader.onloadend = async () => {
        const base64 = reader.result;
        const response = await uploadTemplateImage(base64);
        setFormData({
          ...formData,
          thumbnailUrl: response.data.thumbnailUrl,
        });
        toast({
          title: "Success",
          description: "Thumbnail uploaded successfully",
        });
      };
      reader.readAsDataURL(file);
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to upload thumbnail",
        variant: "destructive",
      });
    } finally {
      setUploadingThumbnail(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Validate JSON fields
    try {
      if (formData.webhookHeaders) JSON.parse(formData.webhookHeaders);
      if (formData.webhookPayload) JSON.parse(formData.webhookPayload);
      if (formData.responseMapping) JSON.parse(formData.responseMapping);
      if (formData.tags) JSON.parse(formData.tags);
    } catch (error) {
      toast({
        title: "Error",
        description: "Invalid JSON in one of the fields",
        variant: "destructive",
      });
      return;
    }

    const payload = {
      ...formData,
      webhookHeaders: formData.webhookHeaders ? JSON.parse(formData.webhookHeaders) : null,
      webhookPayload: formData.webhookPayload ? JSON.parse(formData.webhookPayload) : null,
      responseMapping: formData.responseMapping ? JSON.parse(formData.responseMapping) : null,
      tags: formData.tags ? JSON.parse(formData.tags) : null,
    };

    saveMutation.mutate(payload);
  };

  return (
    <AdminLayout>
      <div className="space-y-6">
        <div className="flex items-center gap-4">
          <Button variant="ghost" onClick={() => navigate("/admin/templates")}>
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <h1 className="text-3xl font-bold">
            {isEdit ? "Edit Template" : "Create Template"}
          </h1>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Basic Information</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label htmlFor="name">Name *</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                />
              </div>

              <div>
                <Label htmlFor="description">Description</Label>
                <Textarea
                  id="description"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  rows={3}
                />
              </div>

              <div>
                <Label htmlFor="category">Category</Label>
                <Input
                  id="category"
                  value={formData.category}
                  onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                />
              </div>

              <div className="flex items-center space-x-2">
                <Switch
                  id="isPublished"
                  checked={formData.isPublished}
                  onCheckedChange={(checked) => setFormData({ ...formData, isPublished: checked })}
                />
                <Label htmlFor="isPublished">Published</Label>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Template Image</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label htmlFor="image">Upload Template Image *</Label>
                <Input
                  id="image"
                  type="file"
                  accept="image/*"
                  onChange={handleImageUpload}
                  disabled={uploading}
                />
                <p className="text-sm text-gray-500 mt-1">
                  This is the main template image used for processing
                </p>
              </div>

              {formData.imageUrl && (
                <div className="mt-4">
                  <Label>Template Image Preview</Label>
                  <img
                    src={formData.imageUrl}
                    alt="Template preview"
                    className="w-full max-w-md rounded-lg shadow-lg border-2 border-gray-200"
                  />
                </div>
              )}

              <div>
                <Label htmlFor="imageUrl">Template Image URL</Label>
                <Input
                  id="imageUrl"
                  value={formData.imageUrl}
                  onChange={(e) => setFormData({ ...formData, imageUrl: e.target.value })}
                  placeholder="https://..."
                />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Thumbnail Image</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label htmlFor="thumbnail">Upload Thumbnail (Optional)</Label>
                <Input
                  id="thumbnail"
                  type="file"
                  accept="image/*"
                  onChange={handleThumbnailUpload}
                  disabled={uploadingThumbnail}
                />
                <p className="text-sm text-gray-500 mt-1">
                  Upload a custom thumbnail for the desktop app. If not provided, a thumbnail will be auto-generated from the template image.
                </p>
              </div>

              {formData.thumbnailUrl && (
                <div className="mt-4">
                  <Label>Thumbnail Preview</Label>
                  <img
                    src={formData.thumbnailUrl}
                    alt="Thumbnail preview"
                    className="w-48 h-48 rounded-lg shadow-lg border-2 border-gray-200 object-cover"
                  />
                </div>
              )}

              <div>
                <Label htmlFor="thumbnailUrl">Thumbnail URL</Label>
                <Input
                  id="thumbnailUrl"
                  value={formData.thumbnailUrl}
                  onChange={(e) => setFormData({ ...formData, thumbnailUrl: e.target.value })}
                  placeholder="https://..."
                />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Webhook Configuration</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label htmlFor="webhookUrl">Webhook URL</Label>
                <Input
                  id="webhookUrl"
                  value={formData.webhookUrl}
                  onChange={(e) => setFormData({ ...formData, webhookUrl: e.target.value })}
                  placeholder="https://api.example.com/process"
                />
              </div>

              <div>
                <Label htmlFor="webhookHeaders">Webhook Headers (JSON)</Label>
                <Textarea
                  id="webhookHeaders"
                  value={formData.webhookHeaders}
                  onChange={(e) => setFormData({ ...formData, webhookHeaders: e.target.value })}
                  rows={3}
                  placeholder='{"Authorization": "Bearer token"}'
                />
              </div>

              <div>
                <Label htmlFor="webhookPayload">Webhook Payload Template (JSON)</Label>
                <Textarea
                  id="webhookPayload"
                  value={formData.webhookPayload}
                  onChange={(e) => setFormData({ ...formData, webhookPayload: e.target.value })}
                  rows={3}
                  placeholder='{"custom_field": "value"}'
                />
              </div>

              <div>
                <Label htmlFor="responseMapping">Response Mapping (JSON)</Label>
                <Textarea
                  id="responseMapping"
                  value={formData.responseMapping}
                  onChange={(e) => setFormData({ ...formData, responseMapping: e.target.value })}
                  rows={3}
                  placeholder='{"outputImage": "data.result.url", "processingTime": "data.time"}'
                />
                <p className="text-sm text-gray-500 mt-1">
                  Map response fields to extract output image URL and processing time
                </p>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>AI Prompt</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label htmlFor="prompt">Template Prompt</Label>
                <Textarea
                  id="prompt"
                  value={formData.prompt}
                  onChange={(e) => setFormData({ ...formData, prompt: e.target.value })}
                  rows={5}
                  placeholder="Bu template için AI servisine gönderilecek prompt. Örn: Make the person look like they are in a fantasy world with magical elements..."
                />
                <p className="text-sm text-gray-500 mt-1">
                  Bu prompt, Postify veya diğer AI servislerine görsel işleme talimatları olarak gönderilir.
                </p>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Metadata</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label htmlFor="tags">Tags (JSON Array)</Label>
                <Textarea
                  id="tags"
                  value={formData.tags}
                  onChange={(e) => setFormData({ ...formData, tags: e.target.value })}
                  rows={2}
                  placeholder='["ai", "portrait", "background"]'
                />
              </div>
            </CardContent>
          </Card>

          <div className="flex gap-4">
            <Button type="submit" disabled={saveMutation.isPending || uploading || uploadingThumbnail}>
              <Save className="mr-2 h-4 w-4" />
              {saveMutation.isPending ? "Saving..." : "Save Template"}
            </Button>
            <Button type="button" variant="outline" onClick={() => navigate("/admin/templates")}>
              Cancel
            </Button>
          </div>
        </form>
      </div>
    </AdminLayout>
  );
}

